import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as sessionNotesService from '../../services/sessionNotes';
import * as franchiseService from '../../services/franchise';
import { base64Decode } from '../../helpers/base64url';

interface StudentNote {
  id?: string;
  student_id: string;
  student_name: string;
  subject_id: string;
  subject_name?: string;
  attended_session: boolean;
  homework_status: string; // API values: 'na', 'completed', 'completed_and_marked', 'not_completed'
  session_note: string;
  status?: string;
  reviews?: any[];
}

const EditSessionNote: React.FC = () => {
  const { encodedData } = useParams<{ encodedData: string }>();
  const navigate = useNavigate();
  const canAccess = useSelector((state: any) => state.auth.permissions?.['session-notes']);
  
  const [loading, setLoading] = useState(false);
  const [sessionDate, setSessionDate] = useState('');
  const [franchiseId, setFranchiseId] = useState<number | null>(null);
  const [centreId, setCentreId] = useState<number | null>(null);
  const [franchiseName, setFranchiseName] = useState('');
  const [centreName, setCentreName] = useState('');
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [studentNotes, setStudentNotes] = useState<StudentNote[]>([]);
  const [studentNotesErrors, setStudentNotesErrors] = useState<Record<string, { session_note?: string }>>({});
  const [subjectFilter, setSubjectFilter] = useState<string>('');
  const [availableSubjects, setAvailableSubjects] = useState<Array<{ id: string; name: string }>>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState<any[]>([]);

  useEffect(() => {
    if (encodedData) {
      try {
        const decoded = base64Decode(encodedData);
        const data = JSON.parse(decoded);
        setSessionDate(data.date);
        setFranchiseId(data.franchise_id);
        setCentreId(data.centre_id);
        fetchSessionNotes(data.date, data.franchise_id, data.centre_id);
      } catch (error) {
        toast.error('Invalid session data');
        navigate('/session-notes');
      }
    }
  }, [encodedData]);

  const fetchSessionNotes = async (date: string, franchiseId: number, centreId: number | null) => {
    setLoading(true);
    try {
      const response = await sessionNotesService.getSessionNoteDetails({
        sessionDate: date,
        franchiseId: franchiseId,
        centreId: centreId || undefined,
      });
      if (response?.success && response.collections?.length > 0) {
        const data = response.collections;
        
        // Check if all students are published or review pending
        const allNonEditable = data.every((note: any) => note.status === 'published' || note.status === 'review_pending');
        if (allNonEditable) {
          toast.error('Only draft session notes can be edited. Published and review pending notes cannot be modified.');
          navigate('/session-notes');
          return;
        }
        
        // Set franchise and centre names from first record
        if (data[0]) {
          setFranchiseName(data[0].franchise_name);
          setCentreName(data[0].centre_name);
        }
        
        // Fetch franchise details to check if review is enabled
        try {
          const franchiseResponse = await franchiseService.getFranchiseById(franchiseId);
          if (franchiseResponse?.success) {
            setReviewEnabled(franchiseResponse.collections?.enabled_review_session_notes || false);
          }
        } catch (error) {
          // If franchise fetch fails, default to false
          setReviewEnabled(false);
        }
        
        // Transform API response to studentNotes format - only include draft notes
        const notes: StudentNote[] = data
          .filter((note: any) => note.status === 'draft') // Only include draft notes
          .map((note: any) => ({
            id: note.id?.toString(),
            student_id: note.student_id.toString(),
            student_name: note.student_name,
            subject_id: note.subject_id.toString(),
            subject_name: note.subject_name,
            attended_session: note.attended === 'yes',
            homework_status: note.homework_status,
            session_note: note.session_note || '',
            status: note.status,
            reviews: note.reviews || []
          }));
        setStudentNotes(notes);
        
        // Extract unique subjects for filter
        const subjects = Array.from(
          new Map(
            notes.map(note => [note.subject_id, { id: note.subject_id, name: note.subject_name || 'Unknown' }])
          ).values()
        );
        setAvailableSubjects(subjects);
        
        // If no draft notes found, show message
        if (notes.length === 0) {
          toast.info('No draft session notes available for editing');
          navigate('/session-notes');
          return;
        }
      } else {
        toast.error('No session notes found');
        navigate('/session-notes');
      }
    } catch (error) {
      toast.error('Failed to fetch session notes');
      navigate('/session-notes');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentNoteChange = (studentId: string, subjectId: string, field: string, value: any) => {
    setStudentNotes(prev =>
      prev.map(note =>
        note.student_id === studentId && note.subject_id === subjectId ? { ...note, [field]: value } : note
      )
    );
    
    // Clear error for session_note when user types
    if (field === 'session_note' && value.trim() !== '') {
      setStudentNotesErrors(prev => {
        const newErrors = { ...prev };
        const key = `${studentId}_${subjectId}`;
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  // Filter student notes by subject
  const filteredStudentNotes = subjectFilter
    ? studentNotes.filter(note => note.subject_id === subjectFilter)
    : studentNotes;

  const handleMarkAllAttended = (attended: boolean) => {
    setStudentNotes(prev =>
      prev.map(note => {
        // Only update notes that match the current filter (or all if no filter)
        const shouldUpdate = !subjectFilter || note.subject_id === subjectFilter;
        if (!shouldUpdate) return note;
        
        return {
          ...note,
          attended_session: attended,
          // Reset other fields if marking as not attended
          homework_status: attended ? note.homework_status : 'na',
          session_note: attended ? note.session_note : ''
        };
      })
    );
    
    // Clear errors for filtered notes when marking as not attended
    if (!attended) {
      setStudentNotesErrors(prev => {
        const newErrors = { ...prev };
        filteredStudentNotes.forEach(note => {
          const key = `${note.student_id}_${note.subject_id}`;
          delete newErrors[key];
        });
        return newErrors;
      });
    }
  };

  const isAllAttended = filteredStudentNotes.length > 0 && filteredStudentNotes.every(note => note.attended_session);
  const isSomeAttended = filteredStudentNotes.some(note => note.attended_session) && !isAllAttended;

  const validateStudentNotes = () => {
    const errors: Record<string, { session_note?: string }> = {};
    let hasErrors = false;
    const attendedStudents = filteredStudentNotes.filter(note => note.attended_session);

    for (const note of attendedStudents) {
      if (!note.session_note || note.session_note.trim() === '') {
        const key = `${note.student_id}_${note.subject_id}`;
        errors[key] = { session_note: 'Session note is required' };
        hasErrors = true;
      }
    }

    setStudentNotesErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (status: 'draft' | 'review_pending' | 'published') => {
    if (filteredStudentNotes.length === 0) {
      toast.error('No students found for this session');
      return;
    }

    // Validate student notes - only session note is required for attended students
    if (!validateStudentNotes()) {
      return;
    }

    setLoading(true);
    try {
      // Group students by subject_id - only include filtered notes
      const subjectGroups: Record<string, any[]> = {};
      
      filteredStudentNotes.forEach(note => {
        if (!subjectGroups[note.subject_id]) {
          subjectGroups[note.subject_id] = [];
        }
        
        subjectGroups[note.subject_id].push({
          id: note.id ? parseInt(note.id) : undefined,
          student_id: parseInt(note.student_id),
          attended: note.attended_session ? 'yes' : 'no',
          homework_status: note.homework_status, // Use API value directly
          session_note: note.session_note
        });
      });

      // Transform to subjects array
      const subjects = Object.keys(subjectGroups).map(subjectId => ({
        subject_id: parseInt(subjectId),
        students: subjectGroups[subjectId]
      }));

      const payload = {
        session_date: sessionDate,
        franchise_id: franchiseId,
        centre_id: centreId,
        status: status,
        subjects: subjects
      };

      const response = await sessionNotesService.updateSessionNoteByDetails(payload);
      if (response?.success) {
        toast.success('Session note updated successfully');
        navigate('/session-notes');
      } else {
        toast.error(response?.message || 'Failed to update session note');
      }
    } catch (error) {
      toast.error('Failed to update session note');
    } finally {
      setLoading(false);
    }
  };

  if (!canAccess?.edit) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning">
              You do not have permission to edit session notes.
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (loading && studentNotes.length === 0) {
    return (
      <div className="">
        <section className="content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (studentNotes.length === 0) {
    return null;
  }

  return (
    <div className="">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Edit Session Note</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/session-notes">Session Notes</Link>
                </li>
                <li className="breadcrumb-item active">Edit</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Session Details</h3>
              <div className="card-tools">
                <Link to="/session-notes" className="btn btn-sm btn-default">
                  <i className="fas fa-arrow-left mr-1"></i>
                  Back to List
                </Link>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <strong>Session Date:</strong>
                  <p>{sessionDate ? new Date(sessionDate).toLocaleDateString() : '--'}</p>
                </div>
                <div className="col-md-4">
                  <strong>Franchise:</strong>
                  <p>{franchiseName || '--'}</p>
                </div>
                <div className="col-md-4">
                  <strong>Centre:</strong>
                  <p>{centreName || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Student Notes */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Student Session Notes</h3>
            </div>
            <div className="card-body">
              {/* Subject Filter */}
              {availableSubjects.length > 1 && (
                <div className="row mb-2">
                  <div className="col-md-3">
                    <label>Filter by Subject</label>
                    <select
                      className="form-control form-control-sm"
                      value={subjectFilter}
                      onChange={(e) => setSubjectFilter(e.target.value)}
                    >
                      <option value="">All Subjects</option>
                      {availableSubjects.map(subject => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: '45px' }}>Sr.</th>
                      <th style={{ width: '70px' }} className="text-center">
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <input
                            type="checkbox"
                            checked={isAllAttended}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = isSomeAttended;
                              }
                            }}
                            onChange={(e) => handleMarkAllAttended(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer', marginBottom: '3px' }}
                            title={isAllAttended ? 'Unmark all' : 'Mark all attended'}
                          />
                          <span style={{ fontSize: '11px' }}>Attended</span>
                        </div>
                      </th>
                      <th style={{ width: '150px' }}>Student Name</th>
                      <th style={{ width: '100px' }}>Subject</th>
                      <th style={{ width: '150px' }}>Homework</th>
                      <th style={{ minWidth: '300px' }}>Session Note</th>
                      <th style={{ width: '200px' }}>Review Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudentNotes.map((note, index) => {
                      const errorKey = `${note.student_id}_${note.subject_id}`;
                      return (
                        <tr key={`${note.student_id}_${note.subject_id}`}>
                          <td>{index + 1}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={note.attended_session}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  note.subject_id,
                                  'attended_session',
                                  e.target.checked
                                )
                              }
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          </td>
                          <td>{note.student_name}</td>
                          <td>{note.subject_name || '-'}</td>
                          <td>
                            <select
                              className="form-control form-control-sm"
                              value={note.homework_status}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  note.subject_id,
                                  'homework_status',
                                  e.target.value
                                )
                              }
                              disabled={!note.attended_session}
                            >
                              <option value="na">N/A</option>
                              <option value="completed">Completed</option>
                              <option value="completed_and_marked">Completed and Marked</option>
                              <option value="not_completed">Not Completed</option>
                            </select>
                          </td>
                          <td>
                            <textarea
                              className={`form-control form-control-sm ${studentNotesErrors[errorKey]?.session_note ? 'is-invalid' : ''}`}
                              rows={3}
                              value={note.session_note}
                              onChange={(e) =>
                                handleStudentNoteChange(
                                  note.student_id,
                                  note.subject_id,
                                  'session_note',
                                  e.target.value
                                )
                              }
                              disabled={!note.attended_session}
                              placeholder="Enter session note..."
                            />
                            {studentNotesErrors[errorKey]?.session_note && (
                              <div className="invalid-feedback d-block">
                                {studentNotesErrors[errorKey].session_note}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            {note.reviews && note.reviews.length > 0 ? (
                              <div>
                                {/* Show latest review */}
                                <div className="mb-1">
                                  <div className="text-muted" style={{ fontSize: '11px', marginBottom: '2px' }}>
                                    <i className="fas fa-comment mr-1"></i>
                                    {note.reviews[0].reviewer_name} - {moment(note.reviews[0].created_at).format('DD/MM/YYYY')}
                                  </div>
                                  <div style={{ 
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: '1.5',
                                    maxHeight: '60px',
                                    overflowY: 'auto'
                                  }}>
                                    {note.reviews[0].review_comments}
                                  </div>
                                </div>
                                {/* Show More button if there are multiple reviews */}
                                {note.reviews.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      setSelectedReviews(note.reviews || []);
                                      setShowReviewModal(true);
                                    }}
                                  >
                                    <i className="fas fa-eye mr-1"></i>
                                    Show More ({note.reviews.length - 1} older)
                                  </button>
                                )}
                              </div>
                            ) : '--'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={() => handleSubmit('draft')}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save as Draft'}
              </button>
              {reviewEnabled ? (
                <button
                  type="button"
                  className="btn btn-success mr-2"
                  onClick={() => handleSubmit('review_pending')}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit for Review'}
                </button>
              ) : (
                canAccess?.publish && (
                  <button
                    type="button"
                    className="btn btn-primary mr-2"
                    onClick={() => handleSubmit('published')}
                    disabled={loading}
                  >
                    {loading ? 'Publishing...' : 'Publish'}
                  </button>
                )
              )}
              <Link to="/session-notes" className="btn btn-default">
                Cancel
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Review Comments Modal */}
      {showReviewModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">All Review Comments</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowReviewModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {selectedReviews.map((review: any, idx: number) => (
                  <div key={review.id || idx} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <strong>{review.reviewer_name}</strong>
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                          <i className="fas fa-calendar mr-1"></i>
                          {moment(review.created_at).format('DD/MM/YYYY HH:mm')}
                        </div>
                      </div>
                    </div>
                    <div style={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: '1.6',
                      padding: '10px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px'
                    }}>
                      {review.review_comments}
                    </div>
                    {idx < selectedReviews.length - 1 && <hr />}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReviewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditSessionNote;
